
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Crown, Loader2 } from "lucide-react";

interface UserActivationButtonProps {
  userEmail: string;
  plan: string;
  onSuccess?: () => void;
}

const UserActivationButton = ({ userEmail, plan, onSuccess }: UserActivationButtonProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const activateUser = async () => {
    setIsLoading(true);
    
    try {
      // First, find the user by email
      const { data: user, error: findError } = await supabase
        .from('profiles')
        .select('*')
        .eq('email', userEmail)
        .single();

      if (findError || !user) {
        toast({
          title: "Erro",
          description: `Usuário ${userEmail} não encontrado.`,
          variant: "destructive",
        });
        return;
      }

      // Calculate subscription end date based on plan
      const endDate = new Date();
      if (plan === 'quarterly') {
        endDate.setMonth(endDate.getMonth() + 3);
      } else {
        endDate.setMonth(endDate.getMonth() + 1);
      }

      // Update the user's plan and subscription status with all necessary fields
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          plan: plan,
          billing_period: plan === 'quarterly' ? 'quarterly' : 'monthly',
          is_subscribed: true,
          subscription_end_date: endDate.toISOString(),
          last_payment_date: new Date().toISOString(),
          last_payment_status: 'succeeded',
          updated_at: new Date().toISOString()
        })
        .eq('email', userEmail);

      if (updateError) {
        console.error('Erro ao atualizar usuário:', updateError);
        toast({
          title: "Erro",
          description: `Erro ao ativar usuário: ${updateError.message}`,
          variant: "destructive",
        });
        return;
      }

      // Create a notification for the user
      const { error: notificationError } = await supabase
        .from('notifications')
        .insert({
          user_id: user.id,
          type: 'subscription_activated',
          message: `Sua assinatura do plano ${plan.toUpperCase()} foi ativada com sucesso!`,
          category: 'subscription'
        });

      if (notificationError) {
        console.error('Erro ao criar notificação:', notificationError);
      }

      toast({
        title: "✅ Usuário Ativado com Sucesso!",
        description: `${userEmail} foi ativado no plano ${plan.toUpperCase()} até ${endDate.toLocaleDateString('pt-BR')}.`,
      });

      if (onSuccess) {
        onSuccess();
      }

    } catch (error) {
      console.error('Erro na ativação:', error);
      toast({
        title: "Erro",
        description: "Erro interno ao ativar usuário.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Button
      onClick={activateUser}
      disabled={isLoading}
      className="bg-gradient-to-r from-gold-500 to-yellow-500 hover:from-gold-600 hover:to-yellow-600 text-black font-semibold shadow-lg"
    >
      {isLoading ? (
        <>
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          Ativando...
        </>
      ) : (
        <>
          <Crown className="w-4 h-4 mr-2" />
          🚀 Ativar {userEmail} - Plano {plan.toUpperCase()}
        </>
      )}
    </Button>
  );
};

export default UserActivationButton;
